import { DatePicker } from '../../components/ui/date-picker';
// apps/web/src/pages/vendas/pedido-item-sheet.tsx — Fase 12, Plano 02 (PV-01, PV-02, D-03..D-07); Plano 07 (D-05, D-06, D-15).
// FormDialog lateral de inclusão/edição de item do PV (1 coluna de campos). No blur do código do cliente consulta
// o vínculo cliente + código (GET /api/v1/vendas/lookups/vinculo) e preenche material, descrição e roteiro;
// o preço vem da tabela (GET /api/v1/vendas/pedidos/preco-sugerido) e só é enviado quando sobrescrito.
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import type { ClienteLookupPv, ConsultaVinculoPv, ContextoPecaPv, CriarPedidoVendaItemInput, MaterialLookupPv, PrecoSugeridoPv, TratamentoPecaDto } from '@pieltec/contracts';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { CampoSelect } from '../../components/ui/campo-select';
import { FormMessage } from '../../components/ui/form-message';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FormDialog, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../components/ui/form-dialog';
import { MaterialCombobox } from '../../components/vendas/material-combobox';
import { RoteiroCombobox } from '../../components/vendas/roteiro-combobox';
import { focarCampo, ORDEM_ITEM_PV, primeiroCampoInvalido, validarItemPv, type CampoItemPv } from '../../lib/vendas/validacao-pv';
import { lerNumeroDigitado, paraTexto } from '../../lib/vendas/numero';
import { COPIA_SEM_PRECO_TABELA, copiaPrecoDiverge, estadoPrecoItem, precoParaEnvio } from '../../lib/vendas/preco-item';
import { VincularMaterialDialog } from './detalhe/vincular-material-dialog';

/** Item do formulário: o contrato de criação + dados só de tela (rótulo do material, preço da tabela). */
export interface ItemPvForm extends CriarPedidoVendaItemInput {
  material?: MaterialLookupPv | null;
  /** Preço da tabela sugerido quando o item foi salvo (só para a grade de resumo; não vai ao servidor). */
  precoTabela?: number | null;
}

interface PedidoItemSheetProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  /** Cliente do PV: sem ele não há consulta de vínculo nem de preço. */
  readonly cliente: ClienteLookupPv | null;
  /** Item em edição (null = novo item). */
  readonly inicial: ItemPvForm | null;
  /**
   * `continuar` = "Adicionar e continuar": o FormDialog permanece aberto com os campos limpos. Quando a gravação é assíncrona,
   * devolver `Promise<boolean>`: o FormDialog só limpa/fecha com `true` (falha da API preserva o que foi digitado).
   */
  readonly onSalvar: (item: ItemPvForm, continuar: boolean) => void | boolean | Promise<boolean | void>;
}

type Erros = Partial<Record<CampoItemPv, string>>;

const ID_CAMPO_ITEM: Record<CampoItemPv, string> = {
  codigoItemCliente: 'item-codigo',
  descricao: 'item-descricao',
  quantidade: 'item-quantidade',
  unidade: 'item-unidade',
  precoUnitario: 'item-preco',
};

type SituacaoVinculo = 'idle' | 'vinculado' | 'nao-vinculado' | 'erro';

const UNIDADE_PADRAO = 'peça';
const ATRASO_PRECO_MS = 300;
const COPIA_NAO_VINCULADO = 'Material não vinculado — este item não libera OP.';
const COPIA_FALHA_CONSULTA = 'Não foi possível consultar o vínculo. Informe o material manualmente.';

export function PedidoItemSheet({ aberto, onAbertoChange, cliente, inicial, onSalvar }: PedidoItemSheetProps) {
  const [codigo, setCodigo] = useState('');
  const [material, setMaterial] = useState<MaterialLookupPv | null>(null);
  const [descricao, setDescricao] = useState('');
  const [cor, setCor] = useState('');
  const [quantidade, setQuantidade] = useState('');
  const [unidade, setUnidade] = useState(UNIDADE_PADRAO);
  const [roteiro, setRoteiro] = useState<string | null>(null);
  const [tratamentos, setTratamentos] = useState<readonly TratamentoPecaDto[]>([]);
  const [codigosCliente, setCodigosCliente] = useState<readonly string[]>([]);
  const [carregandoContexto, setCarregandoContexto] = useState(false);
  const [erroContexto, setErroContexto] = useState<string | null>(null);
  const [consultaContexto, setConsultaContexto] = useState(0);
  const [tratamentoPecaVersaoId, setTratamentoPecaVersaoId] = useState<string | null>(null);
  const [precoTexto, setPrecoTexto] = useState('');
  const [precoSobrescrito, setPrecoSobrescrito] = useState(false);
  const [precoTabela, setPrecoTabela] = useState<number | null>(null);
  const [previsao, setPrevisao] = useState('');
  const [erros, setErros] = useState<Erros>({});
  const [consultando, setConsultando] = useState(false);
  const [situacao, setSituacao] = useState<SituacaoVinculo>('idle');
  const [dialogoAberto, setDialogoAberto] = useState(false);
  // Trava o duplo envio enquanto a gravação assíncrona não responde (WR-D1-03).
  const [salvando, setSalvando] = useState(false);
  // Descarta respostas de consultas antigas (o código pode mudar durante a consulta).
  const consultaAtual = useRef(0);

  useEffect(() => {
    if (!aberto) return;
    consultaAtual.current += 1;
    setCodigo(inicial?.codigoItemCliente ?? '');
    setMaterial(inicial?.material ?? null);
    setDescricao(inicial?.descricao ?? '');
    setCor(inicial?.cor ?? '');
    setQuantidade(inicial ? paraTexto(inicial.quantidade) : '');
    setUnidade(inicial?.unidade ?? UNIDADE_PADRAO);
    setRoteiro(inicial?.roteiroId ?? null);
    setTratamentoPecaVersaoId(inicial?.tratamentoPecaVersaoId ?? null);
    const precoInicial = inicial?.precoUnitario ?? null;
    setPrecoSobrescrito(precoInicial !== null);
    setPrecoTexto(precoInicial === null ? '' : paraTexto(precoInicial));
    setPrecoTabela(inicial?.precoTabela ?? null);
    setPrevisao(inicial?.previsaoEntrega ?? '');
    setErros({});
    setConsultando(false);
    setSituacao(inicial?.material ? 'vinculado' : 'idle');
  }, [aberto, inicial]);

  const clienteId = cliente?.id ?? null;
  const materialId = material?.id ?? null;
  const faltaEscolherTratamento = tratamentos.some((t) => t.ativo && t.vigente) && !tratamentoPecaVersaoId;
  const bloquearSalvamento = salvando || consultando || carregandoContexto || !!erroContexto || faltaEscolherTratamento;

  // Lookup comercial próprio: um vendedor pode escolher a receita do pedido sem
  // precisar de producao:consultar ou abrir a ficha técnica do PCP.
  useEffect(() => {
    if (!aberto || !clienteId || !materialId) { setTratamentos([]); setCodigosCliente([]); setTratamentoPecaVersaoId(null); setCarregandoContexto(false); setErroContexto(null); return; }
    let ativo = true;
    setCarregandoContexto(true);
    setErroContexto(null);
    const params = new URLSearchParams({ clienteId, materialId });
    const preservada = inicial?.material?.id === materialId ? inicial.tratamentoPecaVersaoId : null;
    if (preservada) params.set('versaoPreservadaId', preservada);
    void fetch(`/api/v1/vendas/lookups/contexto-peca?${params.toString()}`, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) throw new Error('Falha ao consultar a peça.');
        const contexto = await res.json() as ContextoPecaPv;
        if (!ativo) return;
        setTratamentos(contexto.tratamentos);
        setCodigosCliente(contexto.codigosItemCliente);
        setCodigo((atual) => atual.trim() ? atual : contexto.codigosItemCliente.length === 1 ? contexto.codigosItemCliente[0]! : '');
        // Uma edição conserva a versão gravada, mesmo quando já existe outra vigente.
        if (preservada) return;
        const opcoes = contexto.tratamentos.filter((t) => t.ativo && t.vigente);
        if (opcoes.length === 1) {
          const versao = opcoes[0]!.vigente!;
          setTratamentoPecaVersaoId(versao.id);
          setRoteiro(versao.roteiroId);
          setCor(versao.cor ?? '');
        } else {
          setTratamentoPecaVersaoId(null);
          setRoteiro(opcoes.length > 1 ? null : (inicial?.material?.id === materialId ? inicial.roteiroId ?? contexto.roteiroSugerido : contexto.roteiroSugerido));
          setCor(opcoes.length > 1 ? '' : (inicial?.material?.id === materialId ? inicial.cor ?? '' : ''));
        }
      })
      .catch(() => { if (ativo) setErroContexto('Não foi possível carregar os dados da peça. Selecione a peça novamente para tentar.'); })
      .finally(() => { if (ativo) setCarregandoContexto(false); });
    return () => { ativo = false; };
  }, [aberto, clienteId, materialId, inicial, consultaContexto]);

  function selecionarTratamento(versaoId: string): void {
    const proximoId = versaoId || null;
    setTratamentoPecaVersaoId(proximoId);
    const versao = tratamentos.find((tratamento) => tratamento.vigente?.id === proximoId)?.vigente;
    if (!versao) return;
    setRoteiro(versao.roteiroId);
    setCor(versao.cor ?? '');
  }

  // Preço da tabela (cliente + material + unidade): atualiza ao trocar o material ou a unidade.
  useEffect(() => {
    if (!aberto || !clienteId || !materialId || !unidade.trim()) {
      setPrecoTabela(null);
      return;
    }
    let ativo = true;
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams({ clienteId, materialId, unidade: unidade.trim() });
      void fetch(`/api/v1/vendas/pedidos/preco-sugerido?${params.toString()}`, { credentials: 'include' })
        .then(async (res) => {
          if (!ativo) return;
          setPrecoTabela(res.ok ? ((await res.json()) as PrecoSugeridoPv).precoUnitario : null);
        })
        .catch(() => {
          if (ativo) setPrecoTabela(null);
        });
    }, ATRASO_PRECO_MS);
    return () => {
      ativo = false;
      window.clearTimeout(timer);
    };
  }, [aberto, clienteId, materialId, unidade]);

  async function consultarVinculo(): Promise<void> {
    const codigoLimpo = codigo.trim();
    if (!cliente || !codigoLimpo || situacao === 'vinculado') return;
    const minhaConsulta = ++consultaAtual.current;
    setConsultando(true);
    try {
      const params = new URLSearchParams({ clienteId: cliente.id, codigoItemCliente: codigoLimpo });
      if (unidade.trim()) params.set('unidade', unidade.trim());
      const res = await fetch(`/api/v1/vendas/lookups/vinculo?${params.toString()}`, { credentials: 'include' });
      if (minhaConsulta !== consultaAtual.current) return;
      if (!res.ok) {
        setSituacao('erro');
        return;
      }
      const resultado = (await res.json()) as ConsultaVinculoPv;
      if (resultado.vinculado && resultado.material) {
        aplicarVinculo(resultado.material);
      } else {
        setSituacao('nao-vinculado');
      }
    } catch {
      if (minhaConsulta === consultaAtual.current) setSituacao('erro');
    } finally {
      if (minhaConsulta === consultaAtual.current) setConsultando(false);
    }
  }

  function aplicarVinculo(vinculado: MaterialLookupPv): void {
    consultaAtual.current += 1;
    setConsultaContexto((n) => n + 1);
    setMaterial(vinculado);
    setDescricao(vinculado.nome);
    if (vinculado.id !== materialId) {
      setTratamentos([]);
      setCodigosCliente([]);
      setTratamentoPecaVersaoId(null);
      setCor('');
      setRoteiro(null);
      setCarregandoContexto(true);
    }
    setConsultando(false);
    setSituacao('vinculado');
  }

  const precoDigitado = lerNumeroDigitado(precoTexto);
  const precoInformado = Number.isFinite(precoDigitado) ? precoDigitado : null;

  function validar(): ItemPvForm | null {
    if (consultando || carregandoContexto || erroContexto) return null;
    if (faltaEscolherTratamento) {
      document.getElementById('item-tratamento')?.focus();
      return null;
    }
    const qtd = lerNumeroDigitado(quantidade);
    // Preço só é validado quando o usuário digitou algo (vazio = vale o preço da tabela).
    const precoParaValidar = precoSobrescrito && precoTexto.trim() ? (precoInformado ?? Number.NaN) : null;
    const proximos = validarItemPv({
      codigoItemCliente: codigo,
      descricao,
      quantidade: qtd,
      quantidadeTexto: quantidade,
      unidade,
      precoUnitario: precoParaValidar,
      precoTexto,
    });
    setErros(proximos);
    const invalido = primeiroCampoInvalido(proximos, ORDEM_ITEM_PV);
    if (invalido) {
      focarCampo(ID_CAMPO_ITEM[invalido]);
      return null;
    }
    return {
      codigoItemCliente: codigo.trim(),
      materialId: material?.id ?? null,
      material,
      descricao: descricao.trim(),
      cor: cor.trim() || null,
      quantidade: qtd,
      unidade: unidade.trim(),
      roteiroId: roteiro,
      tratamentoPecaVersaoId,
      precoUnitario: precoParaEnvio(precoSobrescrito, precoInformado),
      precoTabela,
      previsaoEntrega: previsao || null,
    };
  }

  async function salvar(continuar: boolean): Promise<void> {
    if (salvando) return;
    const item = validar();
    if (!item) return;
    setSalvando(true);
    let gravou = true;
    try {
      gravou = (await onSalvar(item, continuar)) !== false;
    } finally {
      setSalvando(false);
    }
    if (!gravou) return;
    if (continuar) {
      consultaAtual.current += 1;
      setCodigo('');
      setMaterial(null);
      setDescricao('');
      setCor('');
      setQuantidade('');
      setRoteiro(null);
      setTratamentoPecaVersaoId(null);
      setPrecoTexto('');
      setPrecoSobrescrito(false);
      setPrecoTabela(null);
      setPrevisao('');
      setErros({});
      setConsultando(false);
      setSituacao('idle');
    }
  }

  function aoEnviar(event: FormEvent): void {
    event.preventDefault();
    void salvar(false);
  }

  const semMaterial = !material && (situacao === 'nao-vinculado' || situacao === 'erro');
  const estadoPreco = estadoPrecoItem({ temMaterial: !!material, tabela: precoTabela, informado: precoInformado, sobrescrito: precoSobrescrito });
  const precoExibido = precoSobrescrito ? precoTexto : precoTabela === null ? '' : paraTexto(precoTabela);

  return (
    <FormDialog open={aberto} onOpenChange={onAbertoChange}>
      <FormDialogContent>
        <form onSubmit={aoEnviar} noValidate className="flex h-full flex-col">
          <FormDialogHeader>
            <FormDialogTitle className="text-xl">{inicial ? 'Editar item' : 'Adicionar item'}</FormDialogTitle>
            <FormDialogDescription>Informe os dados do item conforme o pedido do cliente.</FormDialogDescription>
          </FormDialogHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="item-codigo">Código do item do cliente</Label>
              <div className="relative">
                <Input
                  id="item-codigo"
                  value={codigo}
                  onChange={(e) => {
                    consultaAtual.current += 1;
                    setMaterial(null);
                    setTratamentoPecaVersaoId(null);
                    setCor('');
                    setRoteiro(null);
                    setConsultando(false);
                    setCodigo(e.target.value);
                    setSituacao('idle');
                  }}
                  onBlur={() => void consultarVinculo()}
                  aria-invalid={erros.codigoItemCliente ? true : undefined}
                  autoFocus
                />
                {consultando && (
                  <Loader2
                    className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400"
                    aria-label="Consultando vínculo do código"
                    role="status"
                  />
                )}
              </div>
              {erros.codigoItemCliente && <FormMessage>{erros.codigoItemCliente}</FormMessage>}
              {codigosCliente.length > 1 && <><CampoSelect aria-label="Referência da peça no cliente" value={codigosCliente.includes(codigo) ? codigo : ''} onValueChange={setCodigo} options={codigosCliente.map((c) => ({value: c, label: c}))} placeholder="Escolha o código da peça no cliente" /><p className="text-xs text-muted-foreground">Esta peça tem mais de um código neste cliente. Escolha a referência do pedido.</p></>}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="item-material">Material interno</Label>
              <MaterialCombobox id="item-material" value={material} onChange={(m) => { setCodigo(''); aplicarVinculo(m); }} disabled={consultando} />
              {carregandoContexto && <p role="status" className="text-xs text-muted-foreground">Carregando os dados da peça…</p>}
              {erroContexto && <FormMessage>{erroContexto}</FormMessage>}
            </div>

            {semMaterial && (
              <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                <AlertTriangle aria-hidden="true" />
                <AlertDescription className="text-amber-900">
                  <p className="font-semibold">{COPIA_NAO_VINCULADO}</p>
                  {situacao === 'erro' && <p>{COPIA_FALHA_CONSULTA}</p>}
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button type="button" size="sm" variant="outline" disabled={!cliente || !codigo.trim()} onClick={() => setDialogoAberto(true)}>
                      Vincular material
                    </Button>
                    <Button type="button" size="sm" variant="ghost" asChild>
                      <a href="/cadastros/materiais" target="_blank" rel="noopener noreferrer">
                        Cadastrar material
                      </a>
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="item-descricao">Descrição</Label>
              <Input
                id="item-descricao"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                aria-invalid={erros.descricao ? true : undefined}
              />
              {erros.descricao && <FormMessage>{erros.descricao}</FormMessage>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="item-cor">Cor</Label>
              <Input id="item-cor" value={cor} disabled={tratamentoPecaVersaoId !== null} onChange={(e) => setCor(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="item-quantidade">Quantidade</Label>
                <Input
                  id="item-quantidade"
                  inputMode="decimal"
                  value={quantidade}
                  onChange={(e) => setQuantidade(e.target.value)}
                  className="text-right tabular-nums"
                  aria-invalid={erros.quantidade ? true : undefined}
                />
                {erros.quantidade && <FormMessage>{erros.quantidade}</FormMessage>}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="item-unidade">Unidade</Label>
                <Input
                  id="item-unidade"
                  value={unidade}
                  onChange={(e) => setUnidade(e.target.value)}
                  aria-invalid={erros.unidade ? true : undefined}
                />
                {erros.unidade && <FormMessage>{erros.unidade}</FormMessage>}
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="item-roteiro">Processo / roteiro</Label>
              <RoteiroCombobox id="item-roteiro" value={roteiro} onChange={setRoteiro} disabled={consultando || tratamentoPecaVersaoId !== null} />
            </div>
            {tratamentos.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="item-tratamento">Tratamento da peça</Label>
                <CampoSelect id="item-tratamento" value={tratamentoPecaVersaoId ?? ''} onValueChange={selecionarTratamento} placeholder="Selecione o tratamento aprovado" options={tratamentos.flatMap((tratamento) => tratamento.vigente ? [{ value: tratamento.vigente.id, label: `${tratamento.codigo} · ${tratamento.nome} · v${tratamento.vigente.versao}${tratamento.vigente.cor ? ` · ${tratamento.vigente.cor}` : ''}${tratamento.vigente.exigeIsolamento ? ' · isolamento' : ''}` }] : [])} />
                <p className="text-xs text-muted-foreground">{!tratamentoPecaVersaoId ? 'Esta peça tem mais de um tratamento. Escolha o tratamento deste pedido.' : 'Tratamento definido: roteiro, cor e requisitos serão preservados no pedido.'}</p>
              </div>
            ) : null}
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="item-preco">Preço unitário</Label>
                {estadoPreco.tipo === 'tabela' && (
                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                    Preço da tabela
                  </span>
                )}
              </div>
              <Input
                id="item-preco"
                inputMode="decimal"
                value={precoExibido}
                onChange={(e) => {
                  setPrecoSobrescrito(true);
                  setPrecoTexto(e.target.value);
                }}
                className="text-right tabular-nums"
                aria-invalid={erros.precoUnitario ? true : undefined}
              />
              {erros.precoUnitario && <FormMessage>{erros.precoUnitario}</FormMessage>}
              {estadoPreco.tipo === 'divergente' && (
                <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                  <AlertTriangle aria-hidden="true" />
                  <AlertDescription className="text-amber-900">{copiaPrecoDiverge(estadoPreco.tabela, estadoPreco.informado)}</AlertDescription>
                </Alert>
              )}
              {estadoPreco.tipo === 'sem-preco' && <p className="text-sm text-muted-foreground">{COPIA_SEM_PRECO_TABELA}</p>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="item-previsao">Previsão de entrega</Label>
              <DatePicker id="item-previsao" value={previsao} onValueChange={(value) => setPrevisao(value)} />
            </div>
          </div>

          <FormDialogFooter className="sm:flex-row sm:justify-end border-t">
            <Button type="button" variant="outline" onClick={() => onAbertoChange(false)} disabled={salvando}>
              Voltar
            </Button>
            {!inicial && (
              <Button type="button" variant="secondary" onClick={() => void salvar(true)} disabled={bloquearSalvamento}>
                Adicionar e continuar
              </Button>
            )}
            <Button type="submit" disabled={bloquearSalvamento}>
              {salvando && <Loader2 className="animate-spin" aria-hidden="true" />}
              Salvar item
            </Button>
          </FormDialogFooter>
        </form>
      </FormDialogContent>

      {cliente && (
        <VincularMaterialDialog
          aberto={dialogoAberto}
          onAbertoChange={setDialogoAberto}
          clienteId={cliente.id}
          clienteNome={cliente.nome}
          codigoItemCliente={codigo.trim()}
          onVinculado={(resultado) => {
            aplicarVinculo(resultado.material);
          }}
        />
      )}
    </FormDialog>
  );
}
